import React, { useState, useEffect, useContext } from "react";
import { Grid, Button, Typography, Box, Fab, IconButton } from "@material-ui/core";
import translate from "../i18n/Translator";
import ValidatedInput, { InputRef } from "../components/ValidatedInput";
import { Cfdi } from "../model/Cfdi";
import { ProrateCfdi, ProrationRequest, CfdiProrateRequest, LedgerAccount, CostCenter } from "../model/Prorate";
import {LedgerAccount as TenantLedgerAccount} from "../model/LedgerAccount";
import {CostCenter as TenantCostCenter} from "../model/CostCenter";
import EditIcon from "@material-ui/icons/Edit";
import SaveIcon from "@material-ui/icons/Save";
import Gridable from "../components/Gridable";
import Ellipsis from "../components/Ellipsis";
import NumberFormat from 'react-number-format';
import { WarningSnackbar } from "../components/Snackbars";
import { AppContext } from "../context/AppContext";
import { updateCfdiProration } from "../api/TenantCfdiApi";
import { NumberValidator } from "../components/Validators";
import { Configuration, TenantConfiguration } from "../model/TenantConfiguration";
import { DeleteIconOneTone } from "../components/Icons";

interface PayableDocumentProratesProps {
    cfdi: Cfdi;
    tenantConfiguration: TenantConfiguration;
    onChange(prorate: ProrateCfdi[]): any;
    onError(error: string): any;
}

export default function PayableDocumentProrates(props: PayableDocumentProratesProps) {
    const context = useContext(AppContext);
    const [status, setStatus] = useState<string>("loading");
    const [request, setRequest] = useState<ProrateCfdi[]>([]);
    const [ledgerAccounts, setLedgerAccounts] = useState<TenantLedgerAccount[]>([]);
    const [costCenters, setCostCenters] = useState<TenantCostCenter[]>([]);
    const [prorateMode, setProrateMode] = useState<string>("NONE");
    const [ledgerAccount, setLedgerAccount] = useState<TenantLedgerAccount>({} as TenantLedgerAccount);
    const [ledgerAccountSelected, setLedgerAccountSelected] = useState<string>();
    const [costCenter, setCostCenter] = useState<TenantCostCenter>({} as TenantCostCenter);
    const [costCenterSelected, setCostCenterSelected] = useState<string>();
    const [amountToAdd, setAmountToAdd] = useState<number>(0);
    const [editable, setEditable] = useState<boolean>(false);
    const [hasProrate, setHasProrate] = useState<boolean>(false);
    const [warning, setWarning] = useState<string>();
    const total = props.cfdi.type === "FOREIGN" ? props.cfdi.total : (props.cfdi.metadata?.subtotal_base ?? 0);
    const isArchived = "ARCHIVED" === props.cfdi.metadata?.cfdi_status_group;
    const tenantAllocationType = props.tenantConfiguration.configurations.find(c => c.property_name === "allocation_type");

    const load = () => {
        const proration = props.cfdi.metadata.cfdi_proration;
        let hasProrateTmp = true;
        setProrateMode(proration.provider_ledger_assignment.prorate_mode);
        if("MANUAL_ALLOCATION_FOR_ALL" === tenantAllocationType?.value) {
            setLedgerAccounts(proration.ledger_accounts);
            setCostCenters(proration.cost_centers);
        } else if ("MANUALLY_PRORATE" === proration.provider_ledger_assignment.prorate_mode) {
            let providerLedgerAccountsByCompany = proration.provider_ledger_assignment.ledger_accounts_response.filter(la => la.company_id === props.cfdi.metadata.company_id).map((la) => la.public_id);
            setLedgerAccounts(proration.ledger_accounts.filter((account) => providerLedgerAccountsByCompany.includes(account.id)));
                
        } else if ("MANUALLY_PRORATE_ALL_LEDGER_ACCOUNTS_AND_COST_CENTERS" === proration.provider_ledger_assignment.prorate_mode) {
            setLedgerAccounts(proration.ledger_accounts.filter((account) => false === account.applies_to_all_companies));
            setCostCenters(proration.cost_centers.filter((ceco) => false === ceco.applies_to_all_companies));
        } else {
            hasProrateTmp = "PRORATE_ALL_ASSIGNED" === proration.provider_ledger_assignment.prorate_mode;
        }
        setHasProrate(hasProrateTmp);
        

        if (proration && proration.prorates && proration.prorates.length > 0) {
            buildRequestByProrates();
        }
        if (proration.provider_ledger_assignment_changed) {
            setWarning(translate("cfdis.prorate.message_prorate_has_changed") as string);
        }
        setStatus("loaded");
    };

    const buildRequestByProrates = () => {
        let temp = [] as ProrateCfdi[];
        props.cfdi.metadata.cfdi_proration?.prorates?.forEach((prorate) => {
            temp.push({
                amount: prorate.amount,
                past_amount: prorate.amount,
                ledger_account: {number: prorate.ledger_account.number, name: prorate.ledger_account.name} as LedgerAccount,
                cost_center: {id: prorate.cost_center.id, external_id: prorate.cost_center.external_id, name: prorate.cost_center.name} as CostCenter
            } as ProrateCfdi);
        });
        if (1 === temp.length) {
            temp[0].amount = total;
        }
        setRequest(temp);
    }

    useEffect(load, [props.cfdi.metadata.cfdi_proration]);

    const onSave = () => {
        let sum = 0;
        request.forEach((field) => sum = Number(sum) + (field.amount ? Number(field.amount) : 0));
        if (Number((sum).toFixed(2)) !== Number((total).toFixed(2))) {
            setWarning(translate("cfdis.prorate.warning") as string);
        } else {
            setEditable(false);
            let temp = {
                signature: props.cfdi.metadata.cfdi_proration.provider_ledger_assignment_changed 
                            ? props.cfdi.metadata.cfdi_proration.provider_ledger_assignment.signature 
                            : props.cfdi.metadata.cfdi_proration.signature,
                cfdi_prorates: []
            } as ProrationRequest;
            request.forEach((prorate) => {
                let prorateTemp = {
                    ledger_account_number: prorate.ledger_account.number,
                    cost_center_erp_id: prorate.cost_center.external_id,
                    amount: prorate.amount
                } as CfdiProrateRequest;
                temp.cfdi_prorates.push(prorateTemp);
            })
            updateCfdiProration(context.session!.tenant!.id, props.cfdi.id, temp).then((metadata) => {
                let temp = request;
                temp.forEach((prorate) => prorate.past_amount = prorate.amount);
                if (temp.length === 1) {
                    temp[0].amount = total;
                }
                setRequest(temp);
                props.onChange(request);
            }).catch((error) => {
                buildRequestByProrates();
                props.onError(error.message);
            });
        }
    };

    const onEditFields = () => {
        if(props.cfdi.metadata.read_only?.active){
            setWarning(props.cfdi.metadata.read_only.comments);
            return;
        }
        setEditable(true);
    }

    const onCancelEditFields = () => {
        clearFields();
        buildRequestByProrates();
        setEditable(false);
    }

    const hasChanged = (name: string, value: number, inputRef: InputRef) => {
        let temp = request;
        temp.forEach((field) => {
            if (field.ledger_account.number + field.cost_center.external_id === name && value >= 0) {
                field.amount = Number(value);
            }
        });
        setRequest(temp);
    };

    const onDeleteProrateRow = (prorate: ProrateCfdi) => {
        const filtered = request.filter(
            (item) =>
                !(
                    item.ledger_account.number === prorate.ledger_account.number &&
                    item.cost_center.id === prorate.cost_center.id &&
                    item.cost_center.external_id === prorate.cost_center.external_id
                )
        );
        setRequest(filtered);
    };

    const onClosedSnackbar = () => {
        setWarning(undefined);
    };

    const onAddProrate = () => {
        if (ledgerAccountSelected && "" === ledgerAccountSelected || Object.entries(ledgerAccount).length === 0) {
            setWarning(translate("cfdis.prorate.add.missing_ledger_account") as string);
            return;
        }

        if(costCenterSelected && "" === costCenterSelected || Object.entries(costCenter).length === 0) {
            setWarning(translate("cfdis.prorate.add.missing_cost_center") as string);
            return;
        }

        if(0 >= amountToAdd) {
            setWarning(translate("cfdis.prorate.add.missing_amount") as string);
            return;
        }

        let prorateAllreadyExists = request.find((prorate) => prorate.ledger_account.number === ledgerAccount.number && prorate.cost_center.external_id === costCenter.external_id);

        if(prorateAllreadyExists) {
            setWarning(translate("cfdis.prorate.add.already_exists", {"ledger": ledgerAccount.number, "ceco": costCenter.name}) as string);
            return;
        }

        let temp = request;
        let prorate = {
            ledger_account: {number: ledgerAccount.number, name: ledgerAccount.name} as LedgerAccount,
            cost_center: {id: costCenter.id, external_id: costCenter.external_id, name: costCenter.name} as CostCenter,
            amount: amountToAdd
        } as ProrateCfdi;
        temp.push(prorate);
        setRequest(temp);
        clearFields();
    };

    const clearFields = () => {
        setLedgerAccountSelected(undefined);
        setCostCenterSelected(undefined);
        setCostCenter({} as TenantCostCenter);
        setLedgerAccount({} as TenantLedgerAccount);
        setAmountToAdd(0);
    };

    const onChangeLedgerAccount = (name: string, value: string, inputRef: any) => {
        let account = ledgerAccounts.find((account) => account.id === value);
        if(account) {
            setLedgerAccount(account);
            setLedgerAccountSelected(value);
            if("MANUALLY_PRORATE" === prorateMode) {
                let ledgerAssignment = props.cfdi.metadata.cfdi_proration.provider_ledger_assignment;
                let providerLedgerAccount = ledgerAssignment.ledger_accounts_response.find((la) => la.public_id === account.id);
                if(providerLedgerAccount && !providerLedgerAccount.use_all_cost_centers) {
                    let costCentersIds = providerLedgerAccount.cost_centers_response.map((cc) => cc.public_id);
                    setCostCenters(props.cfdi.metadata.cfdi_proration.cost_centers.filter((cc) => costCentersIds.includes(cc.id)));
                } else if(providerLedgerAccount && providerLedgerAccount.use_all_cost_centers) {
                    setCostCenters(props.cfdi.metadata.cfdi_proration.cost_centers);
                }
            }
        }
    }

    const onChangeCostCenter = (name: string, value: string, inputRef: any) => {
        let ceco = costCenters.find((ceco) => ceco.id === value);
        if (ceco) {
            setCostCenter(ceco);
            setCostCenterSelected(value);
        }
    }

    const hasChangedNewAmount = (name: string, value: number, inputRef: InputRef) => {
        if(value) {
            setAmountToAdd(value);
        }
    };

    return (
        <Grid container>
            <Box mb={2} px={2} width="100%">
                <Grid item xs={12}>
                    <Box mb={1}>
                        <Grid container>
                            <Grid item xs>
                                {translate("cfdis.prorate.title")}
                            </Grid>

                            {!isArchived && !editable && hasProrate && 
                                <Grid item>
                                    <Fab aria-label="add" color="primary" size="small" title={translate("cfdis.prorate.icon_edit") as string} onClick={onEditFields} >
                                        <EditIcon />
                                    </Fab>
                                </Grid>
                            }
                            {editable && hasProrate &&
                                <Grid item>
                                    <Button aria-label="add" color="primary" size="small" onClick={onCancelEditFields} >
                                        <Typography variant="body2">{translate("cfdis.prorate.icon_cancel")} </Typography>
                                    </Button>
                                    <Fab aria-label="add" color="secondary" size="small" title={translate("cfdis.prorate.icon_save") as string} onClick={onSave} >
                                        <SaveIcon />
                                    </Fab>
                                </Grid>
                            }
                        </Grid>
                    </Box>
                </Grid>
                {editable && hasProrate && ( ("MANUAL_ALLOCATION_FOR_ALL" === tenantAllocationType?.value) || ("NONE" !== prorateMode && "PRORATE_ALL_ASSIGNED" !== prorateMode)) &&
                    <Grid item xs={12}>
                        <Box py={2}>
                            <Grid container spacing={2} alignItems="center" justify="flex-end">
                                <Grid item xs={4} sm={4} md={4} lg={4} xl={4}>
                                    <ValidatedInput type="text" id="type" name="type"
                                        value={ledgerAccountSelected}
                                        label={translate("cfdis.prorate.add.ledger-account") as string}
                                        required={true} disabled={false}
                                        autocompleteOptions={ledgerAccounts}
                                        autocompleteValue={ledgerAccounts.find(e => e.id === ledgerAccount.id) ?? undefined}
                                        autocompleteKey={ledgerAccounts.find(e => e.id === ledgerAccount.id)?.id ?? undefined}
                                        onValueChanged={onChangeLedgerAccount}
                                        getId={(el) => el?.id}
                                        getLabel={(el) => `${el?.number} - ${el?.name}`}
                                    />
                                </Grid>
                                <Grid item xs={4} sm={4} md={4} lg={4} xl={4}>
                                    <ValidatedInput type="text" id="type" name="type"
                                        value={costCenterSelected}
                                        label={translate("cfdis.prorate.add.cost-center") as string}
                                        required={true} disabled={false}
                                        autocompleteOptions={costCenters}
                                        autocompleteValue={costCenters.find(e => e.id === costCenter.id) ?? undefined}
                                        autocompleteKey={costCenters.find(e => e.id === costCenter.id)?.id ?? undefined}
                                        onValueChanged={onChangeCostCenter}
                                        getId={(el) => el?.id}
                                        getLabel={(el) => `${el?.external_id} - ${el?.name}`}
                                    />
                                </Grid>
                                <Grid item xs={2} sm={2} md={2} lg={2} xl={2}>
                                    <ValidatedInput
                                        type="number"
                                        id="prorate-amount"
                                        name="prorate-amount"
                                        value={amountToAdd.toString()}
                                        isValueNumber={true}
                                        label={translate("cfdis.prorate.add.amount") as string}
                                        required={true}
                                        margin="dense"
                                        onValueChanged={hasChangedNewAmount}
                                        validator={new NumberValidator(true)}
                                    />
                                </Grid>
                                <Grid item xs={true} sm={true} md={true} lg={true} xl={true}>
                                    <Button id="add-prorate" variant="contained" color="primary" size="large" disabled={false} onClick={onAddProrate}>{translate("cfdis.prorate.add.button") as string}</Button>
                                </Grid>
                            </Grid>
                        </Box>
                    </Grid>
                        
                }
                <Grid item xs={12}>
                    {!hasProrate && context.isGranted("") &&
                        <Typography variant="body2">{translate("cfdis.prorate.message_config")} </Typography>
                    }
                    {!hasProrate && !context.isGranted("") &&
                        <Typography variant="body2">{translate("cfdis.prorate.message_prorate_not_exists")} </Typography>
                    }
                    {hasProrate &&
                        <Gridable
                            items={request}
                            loading={status === "loading"}
                            empty={translate("cfdis.prorate.empty") as string}
                            error={status !== "loading" && status !== "loaded" ? status : undefined}
                            columns={[
                                {
                                    title: translate("cfdis.prorate.columns.cuenta_code") as string,
                                    converter: (prorate) => prorate.ledger_account.number,
                                    xs: 2,
                                    sm: 2,
                                    md: 2,
                                    lg: 2,
                                    xl: 2
                                },
                                {
                                    title: translate("cfdis.prorate.columns.cuenta_description") as string,
                                    converter: (prorate) => prorate.ledger_account.name,
                                    xs: 4,
                                    sm: 4,
                                    md: 4,
                                    lg: 4,
                                    xl: 4
                                },
                                {
                                    title: translate("cfdis.prorate.columns.centro_costo") as string,
                                    converter: (prorate) => (
                                        <Grid item xs>
                                            <Ellipsis text={prorate.cost_center.name} lenght={100} /><br />
                                            <Ellipsis text={prorate.cost_center.external_id} lenght={0} secondary uppercased={false} />
                                        </Grid>
                                    ),
                                    xs: 4,
                                    sm: 4,
                                    md: 4,
                                    lg: 4,
                                    xl: 4
                                },
                                {
                                    title: translate("cfdis.prorate.columns.amount") as string,
                                    converter: (prorate) => (<FieldEditable field={prorate} editable={editable} onValueChanged={hasChanged} onDelete={onDeleteProrateRow} prorateMode={prorateMode} tenantAllocationType={tenantAllocationType}/>),
                                    xs: true,
                                    sm: true,
                                    md: true,
                                    lg: true,
                                    xl: true,
                                    fullWidth: true
                                }
                            ]} />
                    }
                </Grid>
            </Box>
            <WarningSnackbar message={warning} onClose={onClosedSnackbar} />
        </Grid>
    );
}

interface FieldEditableProps {
    editable: boolean;
    prorateMode: string;
    field: ProrateCfdi;
    tenantAllocationType?: Configuration;
    onValueChanged(name: string, value: number, inputRef: InputRef): any;
    onDelete(prorate: ProrateCfdi): any;
}

function FieldEditable(props: FieldEditableProps) {
    if (props.editable) {
        return (
            <Grid container alignItems="center" spacing={1} wrap="nowrap" >
                <Grid item style={{ flexGrow: 1 }}>
                    <ValidatedInput
                        type="number"
                        id={props.field.ledger_account.number + props.field.cost_center.external_id}
                        name={props.field.ledger_account.number + props.field.cost_center.external_id}
                        value={props.field.amount.toString()}
                        
                        label=""
                        required={false}
                        margin="dense"
                        onValueChanged={props.onValueChanged}
                        validator={new NumberValidator(true)}
                    />
                </Grid>
                    {(("MANUAL_ALLOCATION_FOR_ALL" === props.tenantAllocationType?.value) || ("PRORATE_ALL_ASSIGNED" !== props.prorateMode)) &&
                        <Grid item ><IconButton id={props.field.ledger_account.number + props.field.cost_center.external_id} aria-label="delete" color="default" size="small" onClick={ () => props.onDelete(props.field)}>
                            <DeleteIconOneTone fontSize="small" />
                        </IconButton></Grid>
                    }
                
            </Grid>
        );
    }
    return (
        <NumberFormat value={props.field.amount} prefix="$ " decimalScale={2} fixedDecimalScale={true} displayType="text" />
    );
}